<template>
  <div class="px-4 sm:px-6 lg:px-8 py-8">
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Worksheets</h1>
        <p class="mt-2 text-sm text-gray-700 dark:text-gray-300">
          Manage your data directly in Snowflake using a spreadsheet interface.
        </p>
      </div>
      <div class="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
        <button
          type="button"
          @click="showNewWorksheetModal = true"
          class="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
        >
          New Worksheet
        </button>
      </div>
    </div>

    <div class="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <div
        v-for="worksheet in worksheets"
        :key="worksheet.id"
        class="relative flex flex-col overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow transition hover:shadow-md"
      >
        <div class="p-6 flex-1">
          <div class="flex items-center justify-between">
            <h3 class="text-base font-semibold leading-6 text-gray-900 dark:text-white">
              <NuxtLink :to="`/worksheets/${worksheet.id}`">
                <span class="absolute inset-0" />
                {{ worksheet.name }}
              </NuxtLink>
            </h3>
            <p
              :class="[
                statusColors[worksheet.status] || 'bg-gray-50 text-gray-600',
                'rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset'
              ]"
            >
              {{ worksheet.status }}
            </p>
          </div>
          <div class="mt-4 text-sm text-gray-500 dark:text-gray-400 space-y-2">
             <div class="flex justify-between">
                <span>Rows:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ worksheet.rowCount.toLocaleString() }}</span>
             </div>
             <div class="flex justify-between">
                <span>Updated:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ formatDate(worksheet.updatedAt) }}</span>
             </div>
             <div class="flex justify-between">
                <span>Author:</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ worksheet.lastAuthor }}</span>
             </div>
          </div>
        </div>
        <div class="bg-gray-50 dark:bg-gray-700/50 px-6 py-3">
           <div class="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500">
             Open Worksheet <span aria-hidden="true">&rarr;</span>
           </div>
        </div>
      </div>
    </div>

    <!-- New Worksheet Modal -->
    <NewWorksheetModal
      :open="showNewWorksheetModal"
      @close="showNewWorksheetModal = false"
      @create="handleCreateWorksheet"
    />
  </div>
</template>

<script setup lang="ts">
import NewWorksheetModal from '~/components/worksheets/NewWorksheetModal.vue';

const worksheets = ref<any[]>([]);
const showNewWorksheetModal = ref(false);

const statusColors: Record<string, string> = {
  Active: 'bg-green-50 text-green-700 ring-green-600/20',
  Draft: 'bg-yellow-50 text-yellow-800 ring-yellow-600/20',
  Published: 'bg-blue-50 text-blue-700 ring-blue-700/10',
};

async function fetchWorksheets() {
  const { data } = await useFetch('/api/mocks/worksheets');
  worksheets.value = data.value || [];
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  });
}

async function handleCreateWorksheet(newWorksheetData: any) {
    try {
        const { data } = await useFetch('/api/mocks/worksheets/create', {
            method: 'POST',
            body: newWorksheetData
        });
        
        if (data.value) {
            worksheets.value.push(data.value);
            showNewWorksheetModal.value = false;
            // Optionally navigate to the new worksheet
            navigateTo(`/worksheets/${data.value.id}`);
        }
    } catch (e) {
        console.error("Failed to create worksheet", e);
    }
}

onMounted(() => {
  fetchWorksheets();
});
</script>
